<template>
  <div class="relative">
    <Listbox as="div" class="relative">
      <!-- Search Input -->
      <div class="relative">
        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          v-model="search_term"
          :disabled="disabled"
          autocomplete="off"
          :placeholder="translatedPlaceholder"
          type="text"
          :class="[{ 'w-[600px]': shortKing, 'w-[700px]': !shortKing }, { 'bg-gray-100 cursor-not-allowed': disabled }]"
          class="pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
          @click="show_results = true"
          @focus="show_results = true"
          @keydown="handleKeyDown">

        <!-- Clear button -->
        <button
          v-if="search_term && !disabled"
          class="absolute inset-y-0 right-0 flex items-center pr-3"
          @click="search_term = show_results = false">
          <svg class="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Dropdown Panel -->
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95">
        <div
          v-if="show_results"
          :class="[{ 'w-[600px]': shortKing, 'w-[700px]': !shortKing }]"
          class="absolute z-50 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 max-h-80 overflow-hidden">
          <!-- Close button -->
          <div class="absolute top-4 right-4 z-10">
            <button
              class="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
              @click.stop="show_results = false">
              <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Tab Navigation -->
          <div class="border-b-[1px] pt-2 bg-gray-50 dark:bg-gray-750 border-gray-200 dark:border-gray-700">
            <nav class="flex -mb-1 space-x-3 px-4">
              <div
                v-if="!hidePeopleTab"
                :class="[
                  active_tab === 'team_members'
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                ]"
                class="flex justify-start items-center whitespace-nowrap py-3 mb-1 px-0 border-b-[2px] relative transition ease-in-out duration-200 text-sm font-medium select-none border-transparent cursor-pointer"
                @click.stop="handleTabClick('team_members')">
                <!-- Active bottom div for highlighting -->
                <div
                  :class="[
                    active_tab === 'team_members' ? '' : 'scale-0',
                    'absolute pointer-events-none transition transform ease-in-out duration-200 w-full h-[2px] rounded-full bg-blue-600 dark:bg-blue-400 -bottom-0.5'
                  ]" />
                <svg
                  class="w-4 h-4 mr-2"
                  :class="active_tab === 'team_members' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                <span class="truncate">{{ $t("Team") }}</span>
              </div>

              <div
                v-if="!(hidePeopleTab && hideGroupsTab && hideCompaniesTab)"
                :class="[
                  active_tab === 'contacts'
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                ]"
                class="flex justify-start items-center whitespace-nowrap py-3 mb-1 px-0 border-b-[2px] relative transition ease-in-out duration-200 text-sm font-medium select-none border-transparent cursor-pointer"
                @click.stop="handleTabClick('contacts')">
                <!-- Active bottom div for highlighting -->
                <div
                  :class="[
                    active_tab === 'contacts' ? '' : 'scale-0',
                    'absolute pointer-events-none transition transform ease-in-out duration-200 w-full h-[2px] rounded-full bg-blue-600 dark:bg-blue-400 -bottom-0.5'
                  ]" />
                <svg
                  class="w-4 h-4 mr-2"
                  :class="active_tab === 'contacts' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span class="truncate">{{ $t("Contacts") }}</span>
              </div>

              <div
                v-if="!hideGroupsTab"
                :class="[
                  active_tab === 'groups'
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                ]"
                class="flex justify-start items-center whitespace-nowrap py-3 mb-1 px-0 border-b-[2px] relative transition ease-in-out duration-200 text-sm font-medium select-none border-transparent cursor-pointer"
                @click.stop="handleTabClick('groups')">
                <!-- Active bottom div for highlighting -->
                <div
                  :class="[
                    active_tab === 'groups' ? '' : 'scale-0',
                    'absolute pointer-events-none transition transform ease-in-out duration-200 w-full h-[2px] rounded-full bg-blue-600 dark:bg-blue-400 -bottom-0.5'
                  ]" />
                <svg
                  class="w-4 h-4 mr-2"
                  :class="active_tab === 'groups' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span class="truncate">{{ $t("Groups") }}</span>
              </div>

              <div
                v-if="!hideCompaniesTab"
                :class="[
                  active_tab === 'companies'
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                ]"
                class="flex justify-start items-center whitespace-nowrap py-3 mb-1 px-0 border-b-[2px] relative transition ease-in-out duration-200 text-sm font-medium select-none border-transparent cursor-pointer"
                @click.stop="handleTabClick('companies')">
                <!-- Active bottom div for highlighting -->
                <div
                  :class="[
                    active_tab === 'companies' ? '' : 'scale-0',
                    'absolute pointer-events-none transition transform ease-in-out duration-200 w-full h-[2px] rounded-full bg-blue-600 dark:bg-blue-400 -bottom-0.5'
                  ]" />
                <svg
                  class="w-4 h-4 mr-2"
                  :class="active_tab === 'companies' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span class="truncate">{{ $t("Companies") }}</span>
              </div>

              <div
                v-if="autocompleteOptions?.filter((option) => !option.dynamic_metric_selection).length"
                :class="[
                  active_tab === 'dynamic'
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                ]"
                class="flex justify-start items-center whitespace-nowrap py-3 mb-1 px-0 border-b-[2px] relative transition ease-in-out duration-200 text-sm font-medium select-none border-transparent cursor-pointer"
                @click.stop="handleTabClick('dynamic')">
                <!-- Active bottom div for highlighting -->
                <div
                  :class="[
                    active_tab === 'dynamic' ? '' : 'scale-0',
                    'absolute pointer-events-none transition transform ease-in-out duration-200 w-full h-[2px] rounded-full bg-blue-600 dark:bg-blue-400 -bottom-0.5'
                  ]" />
                <svg
                  class="w-4 h-4 mr-2"
                  :class="active_tab === 'dynamic' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span class="truncate">{{ $t("Dynamic") }}</span>
              </div>

              <div
                v-if="autocompleteOptions?.filter((option) => option.dynamic_metric_selection).length"
                :class="[
                  active_tab === 'custom'
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                ]"
                class="flex justify-start items-center whitespace-nowrap py-3 mb-1 px-0 border-b-[2px] relative transition ease-in-out duration-200 text-sm font-medium select-none border-transparent cursor-pointer"
                @click.stop="handleTabClick('custom')">
                <!-- Active bottom div for highlighting -->
                <div
                  :class="[
                    active_tab === 'custom' ? '' : 'scale-0',
                    'absolute pointer-events-none transition transform ease-in-out duration-200 w-full h-[2px] rounded-full bg-blue-600 dark:bg-blue-400 -bottom-0.5'
                  ]" />
                <svg
                  class="w-4 h-4 mr-2"
                  :class="active_tab === 'custom' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                </svg>
                <span class="truncate">{{ $t("Custom") }}</span>
              </div>

              <!-- Manuel Tab -->
              <div
                :class="[
                  active_tab === 'manuel'
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                ]"
                class="flex justify-start items-center whitespace-nowrap py-3 mb-1 px-0 border-b-[2px] relative transition ease-in-out duration-200 text-sm font-medium select-none border-transparent cursor-pointer"
                @click.stop="handleTabClick('manuel')">
                <!-- Active bottom div for highlighting -->
                <div
                  :class="[
                    active_tab === 'manuel' ? '' : 'scale-0',
                    'absolute pointer-events-none transition transform ease-in-out duration-200 w-full h-[2px] rounded-full bg-blue-600 dark:bg-blue-400 -bottom-0.5'
                  ]" />
                <svg
                  class="w-4 h-4 mr-2"
                  :class="active_tab === 'manuel' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span class="truncate">{{ $t("Manual") }}</span>
              </div>
            </nav>
          </div>

          <!-- Content Area -->
          <div class="overflow-y-auto max-h-64">
            <!-- Empty States -->
            <div v-if="active_tab === 'contacts' && !filtered_contacts.length" class="px-6 py-8 text-center">
              <div class="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 class="text-sm font-medium text-gray-900 dark:text-white mb-2">
                {{ $t("No contacts found") }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ $t("No contacts match your search query") }}
              </p>
            </div>

            <div v-else-if="active_tab === 'team_members' && !filtered_team_members.length" class="px-6 py-8 text-center">
              <div class="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <h3 class="text-sm font-medium text-gray-900 dark:text-white mb-2">
                {{ $t("No team members found") }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ $t("No team members match your search query") }}
              </p>
            </div>

            <div v-else-if="active_tab === 'groups' && !filtered_groups.length" class="px-6 py-8 text-center">
              <div class="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 class="text-sm font-medium text-gray-900 dark:text-white mb-2">
                {{ $t("No groups found") }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ $t("No groups match your search query") }}
              </p>
            </div>

            <div v-else-if="active_tab === 'companies' && !filtered_companies.length && search_term" class="px-6 py-8 text-center">
              <div class="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 class="text-sm font-medium text-gray-900 dark:text-white mb-2">
                {{ $t("No companies found") }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ $t("No companies match your search query") }}
              </p>
            </div>

            <div
              v-else-if="active_tab === 'dynamic' && !autocompleteOptions?.filter((option) => !option.dynamic_metric_selection).length"
              class="px-6 py-8 text-center">
              <div class="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 class="text-sm font-medium text-gray-900 dark:text-white mb-2">
                {{ $t("No dynamic options") }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ $t("No dynamic options are available") }}
              </p>
            </div>

            <div
              v-else-if="active_tab === 'custom' && !autocomplete_options_clone.filter((option) => option.dynamic_metric_selection)"
              class="px-6 py-8 text-center">
              <div class="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                </svg>
              </div>
              <h3 class="text-sm font-medium text-gray-900 dark:text-white mb-2">
                {{ $t("No custom options") }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ $t("No custom options are available") }}
              </p>
            </div>

            <!-- Manuel Form -->
            <div v-else-if="active_tab === 'manuel'" class="py-4 px-6">
              <div class="space-y-4">
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {{ $t("First Name") }}
                    </label>
                    <input
                      v-model="manuel_form.first_name"
                      type="text"
                      class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
                      :placeholder="$t('Enter first name')">
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {{ $t("Last Name") }}
                    </label>
                    <input
                      v-model="manuel_form.last_name"
                      type="text"
                      class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
                      :placeholder="$t('Enter last name')">
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {{ $t("Email") }} <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="manuel_form.email"
                    type="email"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
                    :class="{ 'border-red-500 focus:ring-red-500 focus:border-red-500': manuel_form_error }"
                    :placeholder="$t('Enter email address')"
                    @blur="validateManuelForm">
                  <p v-if="manuel_form_error" class="mt-1 text-sm text-red-600 dark:text-red-400">
                    {{ manuel_form_error }}
                  </p>
                </div>

                <div class="flex justify-end pt-2">
                  <button
                    type="button"
                    :disabled="!manuel_form.email"
                    class="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
                    @click="handleManuelSubmit">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {{ $t("Add Contact") }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Contacts List -->
            <div v-if="active_tab === 'contacts'" class="py-2">
              <ListboxOption
                v-for="contact in filtered_contacts.filter((x) => selectedContacts.filter((s) => s == x).length == 0)"
                :key="contact.id"
                as="template"
                @click="handleSelect(contact)">
                <div
                  class="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200 group">
                  <div class="flex-shrink-0">
                    <Avatar :user="Record.from(contact)" :radius="32" />
                  </div>
                  <div class="ml-3 flex-1 min-w-0">
                    <div
                      class="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                      {{ Record.from(contact).full_name }}
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {{ Record.from(contact).email }}
                    </div>
                  </div>
                  <div class="flex-shrink-0">
                    <svg
                      class="w-4 h-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </ListboxOption>
            </div>

            <!-- Team Members List -->
            <div v-else-if="active_tab === 'team_members'" class="py-2">
              <ListboxOption
                v-for="user in filtered_team_members.filter((x) => selectedContacts.filter((s) => s == x).length == 0)"
                :key="user.id"
                as="template"
                @click="handleSelect(user)">
                <div
                  class="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200 group">
                  <div class="flex-shrink-0">
                    <Avatar :user="Record.from(user)" :radius="32" />
                  </div>
                  <div class="ml-3 flex-1 min-w-0">
                    <div
                      class="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                      {{ Record.from(user).full_name }}
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {{ Record.from(user).email }}
                    </div>
                  </div>
                  <div class="flex-shrink-0">
                    <svg
                      class="w-4 h-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </ListboxOption>
            </div>

            <!-- Groups List -->
            <div v-else-if="active_tab === 'groups'" class="py-2">
              <ListboxOption
                v-for="group in filtered_groups?.filter((x) => selectedGroups?.filter((s) => s == x).length == 0)"
                :key="group.id"
                as="template"
                @click="handleSelect(group)">
                <div
                  class="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200 group">
                  <div class="flex-shrink-0">
                    <div class="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                  </div>
                  <div class="ml-3 flex-1 min-w-0">
                    <div
                      class="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                      {{ group.name }}
                    </div>
                  </div>
                  <div class="flex-shrink-0">
                    <svg
                      class="w-4 h-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </ListboxOption>

              <!-- Create New Group Button -->
              <div v-show="active_tab === 'groups' && !filtered_groups.length" class="flex items-center justify-center p-4">
                <button
                  class="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  @click="
                    () => {
                      $emitter.emit('createNewGroup', null)
                      show_results = false
                    }
                  ">
                  <Plus class="w-4 h-4 mr-2" />
                  {{ $t("Create New Group") }}
                </button>
              </div>
            </div>

            <!-- Companies List -->
            <div v-else-if="active_tab === 'companies'" class="py-2">
              <ListboxOption
                v-for="company in filtered_companies.filter((x) => selectedCompanies.filter((s) => s == x).length == 0)"
                :key="company.id"
                as="template"
                @click="handleSelect(company)">
                <div
                  class="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200 group">
                  <div class="flex-shrink-0">
                    <div class="w-8 h-8 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                  </div>
                  <div class="ml-3 flex-1 min-w-0">
                    <div
                      class="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                      {{ company.values[$store.state.integration.metrics_mapping.company_name_metric] }}
                    </div>
                  </div>
                  <div class="flex-shrink-0">
                    <svg
                      class="w-4 h-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </ListboxOption>
            </div>

            <!-- Dynamic List -->
            <div v-else-if="active_tab === 'dynamic'" class="py-2">
              <ListboxOption
                v-for="result in autocompleteOptions
                  .filter((option) => !option.dynamic_metric_selection)
                  .filter(
                    (o) =>
                      o.value.includes('.co_owners') ||
                      o.value.includes('.owner.id') ||
                      o.value.includes('.co_owners') ||
                      o.value.includes('.contacts')
                  )
                  .filter((a) => (search_term ? a.label.toLowerCase().includes(search_term) : true))"
                :key="result.value"
                as="template"
                @click="handleSelect(result)">
                <div
                  class="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200 group">
                  <div class="flex-shrink-0">
                    <div class="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  </div>
                  <div class="ml-3 flex-1 min-w-0">
                    <div
                      class="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                      {{ result.label }}
                    </div>
                  </div>
                  <div class="flex-shrink-0">
                    <svg
                      class="w-4 h-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </ListboxOption>
            </div>

            <!-- Custom List -->
            <div v-else-if="active_tab === 'custom'" class="py-2">
              <ListboxOption
                v-for="result in autocomplete_options_clone
                  .filter((option) => option.dynamic_metric_selection)
                  .filter((a) => (search_term ? a.label.toLowerCase().includes(search_term) : true))"
                :key="result.value"
                :class="[{ 'pointer-events-none opacity-50': !result.value?.email }]"
                as="template"
                @click="!result.value?.email ? null : handleSelect(result)">
                <div
                  class="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200 group">
                  <div class="flex-shrink-0">
                    <div class="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                      </svg>
                    </div>
                  </div>
                  <div class="ml-3 flex-1 min-w-0">
                    <div
                      class="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                      {{ result.label }}
                    </div>
                  </div>
                  <div class="flex-shrink-0">
                    <button
                      class="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                      @click.stop="
                        () => {
                          object_type = result.object_type
                          $emitter.emit(ALL_EMITTERS.MAP_CUSTOM_FIELDS, { record: result, object_type: result.object_type, all_metrics })
                        }
                      ">
                      <MapFieldsIcon v-if="!result.value?.email" class="w-3 h-3 mr-1" />
                      <PencilLine v-else class="w-3 h-3 mr-1" />
                      {{ result.value?.email ? $t("Edit") : $t("Map") }}
                    </button>
                  </div>
                </div>
              </ListboxOption>
            </div>
          </div>
        </div>
      </transition>
    </Listbox>

    <!-- Backdrop -->
    <div v-if="show_results" class="fixed inset-0 z-40" @click="show_results = false" />
  </div>
</template>

<script>
import { Listbox, ListboxOption } from "@headlessui/vue"
import { PencilLine, Plus } from "lucide-vue-next"
import dataService from "@/services/data.service"
import { Record } from "@/models/record"
import json_data from "@/components/shared/json-files/all-obj-fields.json"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import Avatar from "@/components/shared/Avatar.vue"
import MapFieldsIcon from "@/components/shared/icons/MapFieldsIcon.vue"

export default {
  components: {
    Avatar,
    Plus,
    Listbox,
    ListboxOption,
    MapFieldsIcon,
    PencilLine
  },
  props: {
    hideCompaniesTab: {
      type: Boolean,
      default: false
    },
    hidePeopleTab: {
      type: Boolean,
      default: false
    },
    hideGroupsTab: {
      type: Boolean,
      default: false
    },
    shortKing: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      required: false,
      default: "Search"
    },

    disabled: {
      type: Boolean,
      default: false
    },
    selectedContacts: {
      type: Array,
      required: false
    },
    selectedGroups: {
      type: Array,
      required: false
    },
    selectedCompanies: {
      type: Array,
      required: false
    },
    autocompleteOptions: {
      type: Array,
      default: () => [],
      required: false
    }
  },
  emits: ["chosen", "submitted"],

  data() {
    return {
      ALL_EMITTERS,
      Record,
      show_results: false,
      search_term: "",
      filtered_team_members: [],
      filtered_contacts: [],
      filtered_groups: [],
      filtered_companies: [],
      active_tab: "contacts",
      other_metrics: [],
      forecasted_objects: [],
      object_type: "company",
      autocomplete_options_clone: [],
      search_timeout: null,
      manuel_form: {
        first_name: "",
        last_name: "",
        email: ""
      },
      manuel_form_error: ""
    }
  },

  computed: {
    translatedPlaceholder() {
      return this.$t(this.placeholder)
    },
    all_metrics() {
      let forecast_metrics = []
      if (this.forecasted_objects?.includes(this.object_type)) {
        forecast_metrics = [
          {
            reference_name: `forecasts__forecast_status`,
            name: this.$t("Forecast Status"),
            data_type: "String",
            input_type: "Select",
            input_options: { choices: ["forecasted", "actual"] },
            object_type: this.object_type
          },
          {
            reference_name: `forecasts__forecast_type`,
            name: this.$t("Forecast Type"),
            data_type: "String",
            input_type: "Select",
            input_options: { choices: ["non-renewable", "renewable"] },
            object_type: this.object_type
          },
          {
            reference_name: `forecasts__forecast_value`,
            name: this.$t("Forecast Value"),
            data_type: "Number",
            input_type: "Number",
            object_type: this.object_type
          }
        ]
      }
      let owner_co_owner_filer = [
        {
          reference_name: `owner__email`,
          name: this.$t("Owner"),
          data_type: "String",
          input_type: "Text",
          object_type: this.object_type
        },
        {
          reference_name: `co_owners__email`,
          name: this.$t("Co-Owners"),
          data_type: "String",
          input_type: "Text",
          object_type: this.object_type
        }
      ]
      let tags_filter_fields = [
        {
          reference_name: `tags__name`,
          name: this.$t("Assigned tags"),
          data_type: "String",
          input_type: "Text",
          object_type: this.object_type
        }
      ]

      const metrics_calc = this.$store.state.metrics
        .concat(forecast_metrics)
        .concat(owner_co_owner_filer)
        .concat(this.other_metrics)
        .concat(tags_filter_fields)

      let metrics_calc_clean = []

      metrics_calc_clean = metrics_calc.filter(
        (m) =>
          !m.reference_name.includes("owner_email") &&
          !m.reference_name.includes("owner_first_name") &&
          !m.reference_name.includes("owner_last_name") &&
          !m.reference_name.includes("owner_name") &&
          m.data_type !== "Plan" &&
          m.data_type !== "Goal" &&
          m.data_type !== "Task" &&
          m.data_type !== "Interaction" &&
          m.data_type !== "Milestone"
      )

      return metrics_calc_clean
    }
  },
  watch: {
    search_term() {
      clearTimeout(this.search_timeout)
      this.search_timeout = setTimeout(() => {
        this.fetchAllData()
      }, 500)
    }
  },

  created() {
    this.filtered_contacts = []
    this.filtered_groups = this.$store.state.data?.groups
    this.filtered_team_members = this.$store.state.data.users
    this.init()
  },

  methods: {
    init() {
      this.forecasted_objects = this.$utils
        .find(this.$store.state.settings.modules_settings, {
          reference_code: "renewal_base_metric"
        })
        ?.mapped_objects_metrics?.map((type_map) => {
          return this.$store.state.object_types.find((x) => x.id == type_map.object_type)?.type
        })
      Object.entries(json_data).map((item) => {
        this.other_metrics.push({ ...item[1], object_type: this.object_type })
      })

      this.autocomplete_options_clone = this.$utils.clone(this.autocompleteOptions)
      this.fetchAllData()
    },
    fetchAllData() {
      if (this.search_term === "," || this.search_term === " ") {
        this.search_term = ""
      }

      this.filtered_groups = this.$store.state.data?.groups.filter((group) =>
        group.name.toLowerCase().includes(this.search_term?.toLowerCase())
      )
      this.filtered_team_members = this.$store.state.data.users.filter(
        (contact) => contact.email.includes(this.search_term) || Record.from(contact).full_name.includes(this.search_term)
      )

      dataService.getFilteredContacts(this.search_term).then((res) => {
        this.filtered_contacts = res.data.results
      })

      const companies_filters = {}
      companies_filters["object_types"] = ["company"]
      companies_filters["filter"] = [this.$store.state.integration.metrics_mapping.company_name_metric, "in", `'${this.search_term}'`].join(
        " "
      )

      dataService.filterRecords(companies_filters).then((res) => {
        this.filtered_companies = res.data.results
      })

      // this.show_results = true
      if (
        !this.$store.state.data.users.filter(
          (contact) => contact.email.includes(this.search_term) || Record.from(contact).full_name.includes(this.search_term)
        ).length
      ) {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (re.test(this.search_term)) {
          this.filtered_contacts = [{ full_name: this.search_term, email: this.search_term }]
        } else {
          this.filtered_contacts = []
        }
      }
    },
    handleTabClick(tab) {
      this.show_results = true
      this.active_tab = tab
    },
    handleKeyDown(e) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (
        (e.key === "Enter" || e.key === " " || e.key === ",") &&
        this.search_term &&
        re.test(this.search_term) &&
        this.active_tab == "contacts"
      ) {
        this.$emit("submitted", { full_name: this.search_term, email: this.search_term })
        this.search_term = ""
      }
    },
    handleSelect(item) {
      const item_cloned = this.$utils.clone(item)
      if (item?.label && item?.value) {
        // item_cloned["id"] = item_cloned.value
        item_cloned["name"] = item_cloned.label
        this.$emit("chosen", { item: item_cloned, type: "contacts" })
      } else {
        this.$emit("chosen", { item: item_cloned, type: this.active_tab })
      }
      this.search_term = ""
      this.show_results = false
    },
    validateManuelForm() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (this.manuel_form.email && !emailRegex.test(this.manuel_form.email)) {
        this.manuel_form_error = this.$t("Please enter a valid email address")
      } else {
        this.manuel_form_error = ""
      }
    },
    handleManuelSubmit() {
      this.validateManuelForm()

      if (this.manuel_form_error || !this.manuel_form.email) {
        return
      }

      const contact = {
        first_name: this.manuel_form.first_name || "",
        last_name: this.manuel_form.last_name || "",
        email: this.manuel_form.email,
        full_name: `${this.manuel_form.first_name || ""} ${this.manuel_form.last_name || ""}`.trim() || this.manuel_form.email,
        id: `manuel_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      }

      this.$emit("chosen", { item: contact, type: "contacts" })

      // Reset form
      this.manuel_form = {
        first_name: "",
        last_name: "",
        email: ""
      }
      this.manuel_form_error = ""
      this.show_results = false
    }
  }
}
</script>
